import React from 'react';
import { ArrowLeft, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Datenschutz = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Datenschutzerklärung | ICG Hannover</title>
        <meta name="description" content="Datenschutzerklärung der Internationalen Christengemeinde Hannover. Informationen zur Datenverarbeitung gemäß DSGVO." />
        <link rel="canonical" href="https://www.icg-hannover.de/datenschutz" />
      </Helmet>
      <header className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate('/')}
            data-testid="datenschutz-back-btn"
            className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </button>
          <div className="flex items-center gap-4">
            <Shield className="w-10 h-10" />
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold">Datenschutzerklärung</h1>
              <p className="text-white/70 mt-1">Internationale Christengemeinde Hannover</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 space-y-8 text-gray-700 leading-relaxed" data-testid="datenschutz-content">

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Datenschutz auf einen Blick</h2>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Allgemeine Hinweise</h3>
            <p>
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, 
              wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert 
              werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer nachfolgend aufgeführten 
              Datenschutzerklärung.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Datenerfassung auf dieser Website</h3>
            <p className="font-medium text-gray-800 mt-3">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</p>
            <p className="mt-2">
              Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
              können Sie dem Abschnitt „Hinweis zur verantwortlichen Stelle" in dieser Datenschutzerklärung entnehmen.
            </p>
            <p className="font-medium text-gray-800 mt-4">Wie erfassen wir Ihre Daten?</p>
            <p className="mt-2">
              Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z.B. um Daten 
              handeln, die Sie in ein Kontaktformular eingeben. Andere Daten werden automatisch oder nach Ihrer Einwilligung 
              beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z.B. Internetbrowser, 
              Betriebssystem oder Uhrzeit des Seitenaufrufs). Die Erfassung dieser Daten erfolgt automatisch, sobald Sie diese 
              Website betreten.
            </p>
            <p className="font-medium text-gray-800 mt-4">Wofür nutzen wir Ihre Daten?</p>
            <p className="mt-2">
              Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. 
              Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
            </p>
            <p className="font-medium text-gray-800 mt-4">Welche Rechte haben Sie bezüglich Ihrer Daten?</p>
            <p className="mt-2">
              Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten 
              personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser Daten 
              zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese Einwilligung 
              jederzeit für die Zukunft widerrufen. Außerdem haben Sie das Recht, unter bestimmten Umständen die Einschränkung 
              der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. Des Weiteren steht Ihnen ein Beschwerderecht bei 
              der zuständigen Aufsichtsbehörde zu.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Verantwortliche Stelle</h2>
            <p>Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:</p>
            <div className="bg-gray-50 rounded-xl p-6 mt-4">
              <p className="font-semibold text-gray-900">Internationale Christengemeinde Hannover</p>
              <p>Am Klagesmarkt 29</p>
              <p>30159 Hannover</p>
              <p>Deutschland</p>
              <p className="mt-3">E-Mail: kontakt@icg-hannover.de</p>
            </div>
            <p className="mt-4">
              Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam mit anderen 
              über die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten (z.B. Namen, E-Mail-Adressen o.Ä.) 
              entscheidet.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Datenerfassung auf dieser Website</h2>

            <h3 className="text-lg font-semibold text-gray-800 mb-2">Kontaktformular</h3>
            <p>
              Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular 
              inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von 
              Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
            </p>
            <p className="mt-3">
              Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage 
              mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen erforderlich 
              ist. In allen übrigen Fällen beruht die Verarbeitung auf unserem berechtigten Interesse an der effektiven 
              Bearbeitung der an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO) oder auf Ihrer Einwilligung 
              (Art. 6 Abs. 1 lit. a DSGVO), sofern diese abgefragt wurde.
            </p>
            <p className="mt-3">
              Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie uns zur Löschung auffordern, 
              Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die Datenspeicherung entfällt (z.B. nach 
              abgeschlossener Bearbeitung Ihrer Anfrage). Zwingende gesetzliche Bestimmungen – insbesondere 
              Aufbewahrungsfristen – bleiben unberührt.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">Newsletter</h3>
            <p>
              Wenn Sie den auf der Website angebotenen Newsletter beziehen möchten, benötigen wir von Ihnen eine 
              E-Mail-Adresse. Die Anmeldung zu unserem Newsletter erfolgt in einem sogenannten Double-Opt-In-Verfahren. 
              Nach der Anmeldung erhalten Sie eine E-Mail, in der Sie um Bestätigung Ihrer Anmeldung gebeten werden.
            </p>
            <p className="mt-3">
              Die von Ihnen zum Zwecke des Newsletter-Bezugs bei uns hinterlegten Daten werden von uns bis zu Ihrer 
              Austragung aus dem Newsletter gespeichert und nach der Abbestellung des Newsletters gelöscht. Daten, die 
              zu anderen Zwecken bei uns gespeichert wurden, bleiben hiervon unberührt.
            </p>
            <p className="mt-3">
              Die Datenverarbeitung erfolgt auf Grundlage Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). Die erteilte 
              Einwilligung zur Speicherung der Daten und der E-Mail-Adresse sowie deren Nutzung zum Versand des Newsletters 
              können Sie jederzeit widerrufen, etwa über den „Abmelden"-Link im Newsletter.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Hosting und technische Infrastruktur</h2>
            <p>
              Diese Website wird extern gehostet. Die personenbezogenen Daten, die auf dieser Website erfasst werden, 
              werden auf den Servern des Hosters gespeichert. Hierbei kann es sich v.a. um IP-Adressen, Kontaktanfragen, 
              Meta- und Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen, Websitezugriffe und sonstige Daten, 
              die über eine Website generiert werden, handeln.
            </p>
            <p className="mt-3">
              Das externe Hosting erfolgt zum Zwecke der Vertragserfüllung gegenüber unseren potenziellen und bestehenden 
              Mitgliedern (Art. 6 Abs. 1 lit. b DSGVO) und im Interesse einer sicheren, schnellen und effizienten 
              Bereitstellung unseres Online-Angebots durch einen professionellen Anbieter (Art. 6 Abs. 1 lit. f DSGVO).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Allgemeine Hinweise und Pflichtinformationen</h2>

            <h3 className="text-lg font-semibold text-gray-800 mb-2">Datenschutz</h3>
            <p>
              Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre 
              personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie 
              dieser Datenschutzerklärung.
            </p>
            <p className="mt-3">
              Wir weisen darauf hin, dass die Datenübertragung im Internet (z.B. bei der Kommunikation per E-Mail) 
              Sicherheitslücken aufweisen kann. Ein lückenloser Schutz der Daten vor dem Zugriff durch Dritte ist 
              nicht möglich.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">Speicherdauer</h3>
            <p>
              Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer genannt wurde, verbleiben 
              Ihre personenbezogenen Daten bei uns, bis der Zweck für die Datenverarbeitung entfällt. Wenn Sie ein 
              berechtigtes Löschersuchen geltend machen oder eine Einwilligung zur Datenverarbeitung widerrufen, 
              werden Ihre Daten gelöscht, sofern wir keine anderen rechtlich zulässigen Gründe für die Speicherung 
              Ihrer personenbezogenen Daten haben.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">Widerruf Ihrer Einwilligung zur Datenverarbeitung</h3>
            <p>
              Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. Sie können eine 
              bereits erteilte Einwilligung jederzeit widerrufen. Die Rechtmäßigkeit der bis zum Widerruf erfolgten 
              Datenverarbeitung bleibt vom Widerruf unberührt.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">Recht auf Datenübertragbarkeit</h3>
            <p>
              Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung oder in Erfüllung eines Vertrags 
              automatisiert verarbeiten, an sich oder an einen Dritten in einem gängigen, maschinenlesbaren Format 
              aushändigen zu lassen. Sofern Sie die direkte Übertragung der Daten an einen anderen Verantwortlichen 
              verlangen, erfolgt dies nur, soweit es technisch machbar ist.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">Auskunft, Löschung und Berichtigung</h3>
            <p>
              Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf unentgeltliche Auskunft 
              über Ihre gespeicherten personenbezogenen Daten, deren Herkunft und Empfänger und den Zweck der 
              Datenverarbeitung und ggf. ein Recht auf Berichtigung oder Löschung dieser Daten. Hierzu sowie zu 
              weiteren Fragen zum Thema personenbezogene Daten können Sie sich jederzeit an uns wenden.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">Recht auf Einschränkung der Verarbeitung</h3>
            <p>
              Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. 
              Hierzu können Sie sich jederzeit an uns wenden. Das Recht auf Einschränkung der Verarbeitung besteht 
              in folgenden Fällen:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
              <li>Wenn Sie die Richtigkeit Ihrer bei uns gespeicherten personenbezogenen Daten bestreiten.</li>
              <li>Wenn die Verarbeitung Ihrer personenbezogenen Daten unrechtmäßig geschah/geschieht.</li>
              <li>Wenn Sie die Löschung Ihrer personenbezogenen Daten verlangen, wir die Daten aber noch benötigen.</li>
              <li>Wenn Sie Widerspruch gegen die Verarbeitung eingelegt haben und noch nicht feststeht, ob unsere 
                  berechtigten Gründe gegenüber Ihren überwiegen.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Eingebettete Inhalte und externe Dienste</h2>

            <h3 className="text-lg font-semibold text-gray-800 mb-2">OpenStreetMap</h3>
            <p>
              Wir nutzen den Kartendienst von OpenStreetMap (OSM). Anbieter ist die Open-Street-Map Foundation (OSMF), 
              132 Maney Hill Road, Sutton Coldfield, West Midlands, B72 1JU, United Kingdom. Wenn Sie eine Website 
              besuchen, auf der OpenStreetMap eingebunden ist, werden u.a. Ihre IP-Adresse und weitere Informationen 
              über Ihr Verhalten auf dieser Website an die OSMF weitergeleitet.
            </p>
            <p className="mt-3">
              Die Nutzung von OpenStreetMap erfolgt im Interesse einer ansprechenden Darstellung unserer Online-Angebote 
              und einer leichten Auffindbarkeit der von uns auf der Website angegebenen Orte. Dies stellt ein berechtigtes 
              Interesse im Sinne von Art. 6 Abs. 1 lit. f DSGVO dar.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">YouTube / Vimeo</h3>
            <p>
              Auf unserer Website sind unter Umständen Videos von YouTube oder Vimeo eingebunden. Beim Abspielen dieser 
              Videos wird eine Verbindung zu den Servern dieser Dienste hergestellt. Dabei wird dem jeweiligen Server 
              mitgeteilt, welche unserer Seiten Sie besucht haben. Wenn Sie in Ihrem Konto eingeloggt sind, ermöglichen 
              Sie es dem jeweiligen Dienst, Ihr Surfverhalten direkt Ihrem persönlichen Profil zuzuordnen.
            </p>
            <p className="mt-3">
              Die Nutzung erfolgt im Interesse einer ansprechenden Darstellung unserer Online-Angebote. Dies stellt 
              ein berechtigtes Interesse im Sinne von Art. 6 Abs. 1 lit. f DSGVO dar.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. SSL- bzw. TLS-Verschlüsselung</h2>
            <p>
              Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte, wie zum 
              Beispiel Anfragen, die Sie an uns als Seitenbetreiber senden, eine SSL- bzw. TLS-Verschlüsselung. Eine 
              verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://" auf „https://" 
              wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.
            </p>
            <p className="mt-3">
              Wenn die SSL- bzw. TLS-Verschlüsselung aktiviert ist, können die Daten, die Sie an uns übermitteln, 
              nicht von Dritten mitgelesen werden.
            </p>
          </section>

          <section className="bg-blue-50 rounded-xl p-6">
            <p className="text-sm text-gray-600">
              <strong>Stand:</strong> März 2026
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Quelle: Angepasst nach den Vorgaben der DSGVO (Datenschutz-Grundverordnung) für die 
              Internationale Christengemeinde Hannover.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Datenschutz;
